import { useState } from 'react';
import { RouteType, RBRouteType, CoverageType, FormationType } from '../types';
import { useSimulation, useCustomDefenses } from '../context';
import { useDefenseSelection, useSaveTarget } from '../hooks';
import { getAvailableCoverages } from '../rules';
import { CoverageSelector, FormationSelector, PlayButton, AnimationEngine, PlayArt, SavedPlays, DefenseBuilderLink, RouteControls, FieldMarkings, FieldLegend } from '../components';
import type { PlayPayload } from '../components/SavedPlays';
import ReadDebugPanel, { ReadDebugByPosition } from '../components/dev/ReadDebugPanel';
import { isReadDebugEnabled } from '../utils/devFlags';
import './SimulationPage.css';

function SimulationPage() {
  const [playArtMode, setPlayArtMode] = useState<'off' | 'defense' | 'offense' | 'both'>('off');
  // Dev-only diagnostic overlay. `isReadDebugEnabled` is false in any production build, and the
  // branch guarded on it is dead code there, so none of this ships.
  const readDebug = isReadDebugEnabled();
  const [reads, setReads] = useState<ReadDebugByPosition>({});

  const {
    state,
    setSelectedFormation,
    setSelectedRoute,
    setSelectedRoute2,
    setSelectedRoute3,
    setSelectedRoute4,
    setSelectedRBRoute,
    setSelectedCoverage,
    setIsPlaying,
    setPlaybackSpeed,
    updateWRPosition,
    updateWR2Position,
    updateWR3Position,
    updateWR4Position,
    updateCB1Position,
    updateDBLB1Position,
  } = useSimulation();

  const { selectedFormation, selectedRoute, selectedRoute2, selectedRoute3, selectedRoute4, selectedRBRoute, selectedCoverage, selectedCustomDefenseId, isPlaying, playbackSpeed } = state;

  const { loadState: customDefenseState, completeDefenses } = useCustomDefenses();
  const { activeCustomDefense, isFormationMismatch, handleSelectBuiltIn, handleSelectCustomDefense } = useDefenseSelection(selectedFormation);

  const handleFormationChange = (formation: FormationType) => {
    setSelectedFormation(formation);
    // If current coverage isn't available in the new formation, fall back to 'man'
    const available = getAvailableCoverages(formation);
    if (!available.includes(selectedCoverage)) {
      setSelectedCoverage('man' as CoverageType);
    }
  };
  const handleRouteChange = (route: RouteType) => setSelectedRoute(route);
  const handleRoute2Change = (route: RouteType) => setSelectedRoute2(route);
  const handleRoute3Change = (route: RouteType) => setSelectedRoute3(route);
  const handleRoute4Change = (route: RouteType) => setSelectedRoute4(route);
  const handleRBRouteChange = (route: RBRouteType) => setSelectedRBRoute(route);

  const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlaybackSpeed(parseFloat(e.target.value));
  };

  /**
   * Filled-track shading for the speed slider.
   *
   * Written once and shared by the mobile and desktop copies, which each carried their own identical
   * copy of this expression hard-coding `#007bff` — Bootstrap blue, not a colour used anywhere else
   * in the app — and `#e0e0e0`, which was invisible in dark mode. Custom properties resolve normally
   * inside an inline style, so the track now follows the theme.
   */
  const speedFillPercent = ((playbackSpeed - 0.25) / 0.75) * 100;
  const speedTrackStyle = {
    background: `linear-gradient(to right, var(--color-accent) 0%, var(--color-accent) ${speedFillPercent}%, var(--color-border-strong) ${speedFillPercent}%, var(--color-border-strong) 100%)`,
  };

  const handlePlayClick = () => setIsPlaying(true);
  const handleAnimationComplete = () => setIsPlaying(false);

  const handleTogglePlayArt = () => {
    const cycle: Array<'off' | 'defense' | 'offense' | 'both'> = ['off', 'defense', 'offense', 'both'];
    const nextIndex = (cycle.indexOf(playArtMode) + 1) % cycle.length;
    setPlayArtMode(cycle[nextIndex]);
  };

  /**
   * The visible text on the play-art button, which names the NEXT state in the cycle.
   *
   * That is right for a sighted user reading a button, but it is the wrong accessible name — see
   * `playArtStateLabel` below.
   */
  const playArtLabel = {
    off: 'Show Defense Art',
    defense: 'Show Offense Art',
    offense: 'Show Both',
    both: 'Hide Play Art',
  }[playArtMode];

  /**
   * What the button's accessible name says: the state it is CURRENTLY in.
   *
   * The `aria-label` used to be `playArtLabel`, so a screen reader user was only ever told what the
   * next press would do and never what was on screen — and because the name changed on every press,
   * there was no stable control to refer to. This pairs a fixed name with `aria-pressed` plus the
   * current mode, which is the combination that actually communicates state.
   */
  const playArtStateLabel = {
    off: 'Play art: off',
    defense: 'Play art: defense',
    offense: 'Play art: offense',
    both: 'Play art: offense and defense',
  }[playArtMode];

  const currentPlay: PlayPayload = {
    wr1Route: selectedRoute,
    wr2Route: selectedRoute2,
    wr3Route: selectedRoute3,
    wr4Route: selectedRoute4,
    rbRoute: selectedRBRoute,
  };

  // Which saved play the routes on screen came from. Held here rather than inside SavedPlays
  // because SavedPlays is rendered twice below (mobile and desktop layouts) — one shared handle
  // keeps both copies agreeing about what is loaded and whether it has been edited.
  const playSaveTarget = useSaveTarget(currentPlay);

  const handleLoadPlay = (play: PlayPayload) => {
    setSelectedRoute(play.wr1Route);
    setSelectedRoute2(play.wr2Route);
    setSelectedRoute3(play.wr3Route);
    setSelectedRoute4(play.wr4Route);
    setSelectedRBRoute(play.rbRoute);
  };

  return (
    <div className="simulation-page">
      <div className="simulation-area">
        <div className="simulation-container">
          {/* First in the DOM so the turf and yardage sit behind every player and overlay. */}
          <FieldMarkings />
          <div className="sim-coverage-selector">
            <CoverageSelector
              id="field-coverage-select"
              selectedCoverage={selectedCoverage}
              onCoverageChange={handleSelectBuiltIn}
              disabled={isPlaying}
              formation={selectedFormation}
              customDefenseState={customDefenseState}
              completeDefenses={completeDefenses}
              selectedCustomDefenseId={selectedCustomDefenseId}
              onSelectCustomDefense={handleSelectCustomDefense}
            />
            {isFormationMismatch && (
              <span className="formation-mismatch-badge">Built for a different formation</span>
            )}
          </div>
          <button
            className="mobile-play-button"
            type="button"
            aria-label={isPlaying ? 'Playing' : 'Run play'}
            onClick={handlePlayClick}
            disabled={isPlaying}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          <button
            className="mobile-play-art-button"
            type="button"
            aria-label={playArtStateLabel}
            aria-pressed={playArtMode !== 'off'}
            onClick={handleTogglePlayArt}
          >
            <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="7" cy="20" r="1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
              <circle cx="17" cy="20" r="1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
              <polyline points="7,18.5 7,8" stroke="currentColor" strokeWidth="2" />
              <polyline points="7,8 10,11" stroke="currentColor" strokeWidth="2" />
              <polyline points="17,18.5 17,7 3,2" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          {playArtMode !== 'off' && (
            <PlayArt
              selectedRoute={selectedRoute}
              selectedRoute2={selectedRoute2}
              selectedRoute3={selectedRoute3}
              selectedRoute4={selectedRoute4}
              selectedRBRoute={selectedRBRoute}
              selectedCoverage={selectedCoverage}
              selectedFormation={selectedFormation}
              isPlaying={isPlaying}
              mode={playArtMode}
              customDefenseAssignments={activeCustomDefense?.assignments}
            />
          )}
          <AnimationEngine
            isPlaying={isPlaying}
            selectedFormation={selectedFormation}
            selectedRoute={selectedRoute}
            selectedRoute2={selectedRoute2}
            selectedRoute3={selectedRoute3}
            selectedRoute4={selectedRoute4}
            selectedRBRoute={selectedRBRoute}
            selectedCoverage={selectedCoverage}
            playbackSpeed={playbackSpeed}
            customDefenseAssignments={activeCustomDefense?.assignments}
            onReadTrace={readDebug ? setReads : undefined}
            onAnimationComplete={handleAnimationComplete}
            onWRPositionUpdate={updateWRPosition}
            onWR2PositionUpdate={updateWR2Position}
            onWR3PositionUpdate={updateWR3Position}
            onWR4PositionUpdate={updateWR4Position}
            onCB1PositionUpdate={updateCB1Position}
            onDBLB1PositionUpdate={updateDBLB1Position}
            onWR1RouteChange={handleRouteChange}
            onWR2RouteChange={handleRoute2Change}
            onWR3RouteChange={handleRoute3Change}
            onWR4RouteChange={handleRoute4Change}
            onRBRouteChange={handleRBRouteChange}
          />
        </div>
        <FieldLegend />
      </div>

      {readDebug && <ReadDebugPanel reads={reads} />}

      {/* Mobile controls - visible below the field on small screens.
          Ordered setup-then-playback: formation and routes build the play, speed and the rest
          control watching it. Coverage is not here because it sits on the field itself at this
          breakpoint (.sim-coverage-selector above). */}
      <div className="mobile-controls">
        <div className="control-section">
          <span className="control-label" id="mobile-formation-label">Formation</span>
          <FormationSelector
            id="mobile-formation-select"
            labelledBy="mobile-formation-label"
            selectedFormation={selectedFormation}
            onFormationChange={handleFormationChange}
            disabled={isPlaying}
          />
        </div>
        <div className="control-section">
          <span className="control-label">Routes</span>
          <RouteControls
            idPrefix="mobile"
            selectedRoute={selectedRoute}
            selectedRoute2={selectedRoute2}
            selectedRoute3={selectedRoute3}
            selectedRoute4={selectedRoute4}
            selectedRBRoute={selectedRBRoute}
            onRouteChange={handleRouteChange}
            onRoute2Change={handleRoute2Change}
            onRoute3Change={handleRoute3Change}
            onRoute4Change={handleRoute4Change}
            onRBRouteChange={handleRBRouteChange}
            disabled={isPlaying}
          />
        </div>
        <div className="control-section control-section-speed">
          <label className="control-label" htmlFor="mobile-speed-slider">
            Speed: {playbackSpeed.toFixed(2)}x
          </label>
          <input
            type="range"
            id="mobile-speed-slider"
            className="speed-slider"
            min="0.25"
            max="1"
            step="0.05"
            value={playbackSpeed}
            onChange={handleSpeedChange}
            disabled={isPlaying}
            style={speedTrackStyle}
          />
          <div className="speed-labels">
            <span>0.25x</span>
            <span>1x</span>
          </div>
        </div>
        <div className="control-section">
          <SavedPlays
            currentPlay={currentPlay}
            onLoadPlay={handleLoadPlay}
            saveTarget={playSaveTarget}
            disabled={isPlaying}
          />
        </div>
        <div className="control-section">
          <DefenseBuilderLink />
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="control-panel">
        <h2>Play Controls</h2>
        <div className="control-section control-section-dropdowns">
          {/* A <span>, not a <label>: the control it names is a <div role="combobox">, which
              htmlFor cannot target. The association is aria-labelledby instead. */}
          <span className="control-label" id="desktop-formation-label">Formation</span>
          <FormationSelector
            id="desktop-formation-select"
            labelledBy="desktop-formation-label"
            selectedFormation={selectedFormation}
            onFormationChange={handleFormationChange}
            disabled={isPlaying}
          />
        </div>
        {/* Routes sit between formation and coverage so the panel reads in the order a coach
            builds a look: line up, assign the offense, then pick what the defense is playing. */}
        <div className="control-section control-section-routes">
          <span className="control-label">Routes</span>
          <RouteControls
            idPrefix="desktop"
            selectedRoute={selectedRoute}
            selectedRoute2={selectedRoute2}
            selectedRoute3={selectedRoute3}
            selectedRoute4={selectedRoute4}
            selectedRBRoute={selectedRBRoute}
            onRouteChange={handleRouteChange}
            onRoute2Change={handleRoute2Change}
            onRoute3Change={handleRoute3Change}
            onRoute4Change={handleRoute4Change}
            onRBRouteChange={handleRBRouteChange}
            disabled={isPlaying}
          />
        </div>
        <div className="control-section control-section-dropdowns">
          <span className="control-label" id="desktop-coverage-label">Coverage</span>
          <CoverageSelector
            id="desktop-coverage-select"
            labelledBy="desktop-coverage-label"
            selectedCoverage={selectedCoverage}
            onCoverageChange={handleSelectBuiltIn}
            disabled={isPlaying}
            formation={selectedFormation}
            customDefenseState={customDefenseState}
            completeDefenses={completeDefenses}
            selectedCustomDefenseId={selectedCustomDefenseId}
            onSelectCustomDefense={handleSelectCustomDefense}
          />
          {isFormationMismatch && (
            <span className="formation-mismatch-badge">Built for a different formation</span>
          )}
        </div>
        <div className="control-section control-section-speed">
          <label className="control-label" htmlFor="speed-slider">
            Playback Speed: {playbackSpeed.toFixed(2)}x
          </label>
          <input
            type="range"
            id="speed-slider"
            className="speed-slider"
            min="0.25"
            max="1"
            step="0.05"
            value={playbackSpeed}
            onChange={handleSpeedChange}
            disabled={isPlaying}
            style={speedTrackStyle}
          />
          <div className="speed-labels">
            <span>0.25x</span>
            <span>1x</span>
          </div>
        </div>
        <div className="control-section control-section-play">
          <PlayButton onClick={handlePlayClick} isPlaying={isPlaying} />
        </div>
        <div className="control-section control-section-dropdowns">
          <button 
            className={`play-art-toggle ${playArtMode !== 'off' ? 'active' : ''}`}
            onClick={handleTogglePlayArt}
            type="button"
            // The `.active` class was the only signal that this was a toggle.
            aria-pressed={playArtMode !== 'off'}
          >
            {playArtLabel}
          </button>
        </div>
        <div className="control-section control-section-dropdowns">
          <DefenseBuilderLink />
        </div>
        <div className="control-section control-section-dropdowns">
          <SavedPlays
            currentPlay={currentPlay}
            onLoadPlay={handleLoadPlay}
            saveTarget={playSaveTarget}
            disabled={isPlaying}
          />
        </div>
      </aside>
    </div>
  );
}

export default SimulationPage;
